const ItemType = require('@/lib/Items/ItemType');
const KitchenItemOption = require('@/lib/Kitchen/KitchenItemOption');
const KitchenOrder = require('@/libraries/DigiTickets/Kitchen/KitchenOrder');
const KitchenOrderItem = require('@/libraries/DigiTickets/Kitchen/KitchenOrderItem');
const { cloneDeep } = require('@/functions/clone');
const { findKitchenItemInOrder } = require('@/lib/Kitchen');
const { kitchenOrderItemHash } = require('@/libraries/DigiTickets/Kitchen/kitchenOrderItemHash');

/**
 * This was previously called OrderToKitchenOrderMapper and was a simple object with some functions.
 * It's been converted to a class because it now has dependencies.
 */
class KitchenOrderFactory {
    /**
     * @param {ItemOptionRepository} itemOptionRepository
     * @param {KitchenZoneRepository} kitchenZoneRepository
     * @param {ProductRepository} productRepository
     */
    constructor(
        itemOptionRepository,
        kitchenZoneRepository,
        productRepository,
    ) {
        this.itemOptionRepository = itemOptionRepository;
        this.kitchenZoneRepository = kitchenZoneRepository;
        this.productRepository = productRepository;

        /**
         * @type {?KitchenZone[]}
         */
        this.allKitchenZones = null;

        /**
         * @type {?number[]}
         */
        this.allKitchenZoneIDs = null;
    }

    /**
     * Returns all kitchen zones that exist (those that have been synced for the *current branch*),
     * using the cached array of this.allKitchenZones if it exists.
     *
     * @returns {Promise<KitchenZone[]>}
     */
    async getAllKitchenZones() {
        if (!this.allKitchenZones) {
            this.allKitchenZones = await this.kitchenZoneRepository.findAll();
        }

        return this.allKitchenZones;
    }

    /**
     * Returns all kitchen zone IDs that exist (those that have been synced for the *current branch*),
     * using the cached array of this.allKitchenZoneIDs if it exists.
     *
     * @returns {Promise<number[]>}
     */
    async getAllKitchenZoneIDs() {
        if (!this.allKitchenZoneIDs) {
            const allKitchenZones = await this.getAllKitchenZones();
            this.allKitchenZoneIDs = allKitchenZones.map((kitchenZone) => kitchenZone.ID);
        }

        return this.allKitchenZoneIDs;
    }

    /**
     * Generates a KitchenOrder from an Order.
     * This contains information about the order that is used by the KitchenReceiptPrinter.
     *
     * @param {Order} order
     * @param {KitchenZone[]|null} kitchenZones If a list of kitchen zones is provided, only items that should show in
     *                                          those zones will be included in the visibleItems list. Remember that
     *                                          kitchen zones don't apply in the Kitchen Preview Modal because that
     *                                          displays a complete order.
     * @param {boolean} applyFulfillments
     *
     * @return {KitchenOrder}
     */
    async map(
        order,
        kitchenZones = null,
        applyFulfillments = true
    ) {
        const kitchenOrder = new KitchenOrder();

        kitchenOrder.fulfillmentCreatedAt = order.fulfillmentCreatedAt || null;
        kitchenOrder.fulfilledAt = order.fulfilledAt || null;
        kitchenOrder.fulfillments = cloneDeep(order.fulfillments);

        kitchenOrder.modifiedAt = order.date;

        kitchenOrder.orderRef = order.getOriginalRef();
        kitchenOrder.fulfillmentInstructions = cloneDeep(order.getFulfillmentInstructions());

        // Choose an appropriate kitchen reference for the order.
        // In order of preference:
        // - Staff ref (should contain the table number)
        // - Collection number (if present)
        // - Booking Ref (gotta have something!)
        kitchenOrder.kitchenRef = order.bookingRef;

        if (kitchenOrder.fulfillmentInstructions?.tableName) {
            kitchenOrder.kitchenRef = kitchenOrder.fulfillmentInstructions.tableName;
        } else if (kitchenOrder.fulfillmentInstructions?.collectionNumber) {
            kitchenOrder.kitchenRef = kitchenOrder.fulfillmentInstructions.collectionNumber;
        }

        kitchenOrder.thirdPartyID = order.thirdPartyID;
        kitchenOrder.thirdPartyRef = order.thirdPartyRef;
        kitchenOrder.notes = order.notes;
        kitchenOrder.orderID = order.ID;
        kitchenOrder.deviceName = order.device ? order.device.name : null;
        kitchenOrder.seller = cloneDeep(order.seller);
        kitchenOrder.paymentChannel = cloneDeep(order.paymentChannel);

        // All the kitchen zoneIDs used by the order.
        let kitchenZoneIDsInOrder = [];

        // Copy the order lines into a new array we can sort it without affecting the original order.
        const orderLines = [
            ...order.items,
        ];
        // Actually, no need to sort any more!

        // We'll do a first pass and extract all item options from the order, building an object of KitchenItemOptions
        // that belong to each parent line ID.
        // We do this before the actual kitchen items, because these options will affect the hash of the kitchen items.
        const itemOptionsByParentLineID = {};
        for (let i = 0; i < orderLines.length; i++) {
            const orderLine = orderLines[i];

            if (orderLine.getQuantity() <= 0 || !orderLine.parentID) {
                continue;
            }

            try {
                const kitchenItemOption = await this.createKitchenItemOptionFromLine(orderLine);
                if (!kitchenItemOption) {
                    // This line is not an item option.
                    continue;
                }

                itemOptionsByParentLineID[orderLine.parentID] = itemOptionsByParentLineID[orderLine.parentID] || [];
                itemOptionsByParentLineID[orderLine.parentID].push(kitchenItemOption);
            } catch (err) {
                console.warn(`Failed to create kitchen item option from line ID ${orderLine.ID}: ${err.message}`);
                // Just skip this item option.
            }
        }

        // Now do a second pass and handle the actual kitchen items (products).
        for (let i = 0; i < orderLines.length; i++) {
            const orderLine = orderLines[i];

            if (orderLine.getQuantity() <= 0) {
                // Skip negative lines.
                continue;
            }

            /**
             * Load the item for the line from the repository, because the Item on the line came
             * from the order and may not contain all the data we need.
             *
             * @type {ItemOption|Product}
             */
            let item;
            try {
                switch (orderLine.item.itemType) {
                    case ItemType.PRODUCT:
                        item = await this.productRepository.find(orderLine.itemID);
                        break;

                    // We are still only loading products here because item options are now handled above.
                    // Those don't generate KitchenOrderItems - they generate KitchenItemOptions that get attached to
                    // their parent KitchenOrderItem.
                }
            } catch (err) {
                // find() will throw an error if the product cannot be found.
                // This can happen if the product has been archived/deleted since the order was placed.
                console.warn(`Failed to load ${orderLine.item.itemType} with ID ${orderLine.item.ID}: ${err.message}`);
                continue;
            }
            if (!item) {
                // The item could not be found. Maybe it wasn't an appropriate type. Skip it.
                continue;
            }

            if (!item.showOnKitchenScreen) {
                // This item is not set to show on the kitchen screen. Skip it.
                continue;
            }

            let kitchenItem = new KitchenOrderItem();
            kitchenItem.itemName = orderLine.getName();
            kitchenItem.itemID = orderLine.getItemID();
            kitchenItem.qty = orderLine.getQuantity();

            if (order.fields) {
                let lineFieldGroups = order.fields.filterFieldGroups({
                    lineNumber: orderLine.lineNumber,
                    hideNoValue: true,
                });

                for (let j = 0; j < lineFieldGroups.length; j++) {
                    kitchenItem.addFieldData(lineFieldGroups[j].getDisplayValues());
                }
            }

            // Attach any item options that belong to this line.
            if (itemOptionsByParentLineID[orderLine.ID]) {
                kitchenItem.itemOptions = itemOptionsByParentLineID[orderLine.ID];
            }

            // Generate a hash for this item so we can tell if an equivalent item is already in the kitchen order.
            // The hash comprises of:
            // - itemID
            // - field data (keys and values)
            // - item options (optionSetIDs and itemOptionIDs)
            kitchenItem.hash = kitchenOrderItemHash(kitchenItem);

            let existingItem = findKitchenItemInOrder(kitchenOrder, kitchenItem);
            if (existingItem) {
                existingItem.qty += kitchenItem.qty;
            } else {
                kitchenOrder.addItem(kitchenItem);
            }

            // Set the list of kitchenZoneIDs on the KitchenOrderItem from the Product.
            kitchenItem.kitchenZoneIDs = item.kitchenZoneIDs;

            // If a product has no zones it should display in all zones.
            if (!kitchenItem.kitchenZoneIDs || kitchenItem.kitchenZoneIDs.length === 0) {
                kitchenItem.kitchenZoneIDs = await this.getAllKitchenZoneIDs();
            }

            kitchenZoneIDsInOrder.push(...kitchenItem.kitchenZoneIDs);

            // Add it to the visible items if:
            // 1. It should be displayed on the current kitchen screen.
            // 2. It should be displayed in the Kitchen Order Preview Modal where no zones apply.
            if (!kitchenZones || kitchenZones.length === 0) {
                kitchenItem.isVisible = true;
            } else {
                for (let z = 0; z < kitchenZones.length; z++) {
                    if (item.showInKitchenZone(kitchenZones[z].ID)) {
                        kitchenItem.isVisible = true;
                        break;
                    }
                }
            }
        }

        if (applyFulfillments) {
            kitchenOrder.setFulfillments(order.fulfillments);
        }

        kitchenOrder.kitchenZoneIDsInOrder = [...new Set(kitchenZoneIDsInOrder)];

        return kitchenOrder;
    }

    /**
     * @param {OrderLine} orderLine
     *
     * @returns {KitchenItemOption|null}
     */
    async createKitchenItemOptionFromLine(orderLine) {
        if (orderLine.itemType !== ItemType.ITEM_OPTION) {
            return null;
        }

        const itemOption = await this.itemOptionRepository.find(orderLine.getItemID());
        if (!itemOption || !itemOption.showOnKitchenScreen) {
            return null;
        }

        return new KitchenItemOption({
            itemID: orderLine.getItemID(),
            itemName: orderLine.getName(),
            optionSetID: orderLine.optionSetID,
        });
    }

    /**
     * Update the visibility prop of a kitchen order's items based on if that item is visible in the given zone.
     *
     * This method gets called when iterating through the kitchen zones when printing directly from the ProPoint
     * Sell Screen. See KitchenReceiptPrinter.
     *
     * @param {KitchenOrder} kitchenOrder
     * @param {number} zoneID
     */
    updateItemVisibilityForZone(kitchenOrder, zoneID) {
        kitchenOrder.items.forEach((item) => {
            item.isVisible = item.kitchenZoneIDs.includes(zoneID);
        });
    }

    /**
     * @param {Order[]} orders
     * @param {KitchenZone[]} kitchenZones
     *
     * @return {Promise<KitchenOrder[]>}
     */
    async mapMany(orders, kitchenZones) {
        return Promise.all(
            orders.map((order) => this.map(order, kitchenZones))
        );
    }
}

module.exports = KitchenOrderFactory;
